import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
// API
import { TRACK_EXT_TALENT_CTA_CLICK } from 'api/brandDeals/mutations';
// Types
import {
  TrackExtTalentCtaClick,
  TrackExtTalentCtaClickVariables,
} from 'api/brandDeals/types/TrackExtTalentCtaClick';
// Helpers
import {
  getExtTalentBrandDealAttributionFromQuery,
  setExtTalentBrandDealAttributionToStorage,
} from 'helpers/extTalentBrandDeal';
// Hooks
import { useAppContext } from 'hooks';

export const useExtTalentCtaTracking = (): void => {
  const router = useRouter();
  const { token, setSignUpModalVisibility } = useAppContext();
  const lastFiredKeyRef = useRef<string | null>(null);

  const [trackExtTalentCtaClick] = useMutation<
    TrackExtTalentCtaClick,
    TrackExtTalentCtaClickVariables
  >(TRACK_EXT_TALENT_CTA_CLICK, {
    // Tracking is fire-and-forget: must never break the landing page.
    onError: (error) => {
      console.error('[ExtTalentBdTracking] CTA click track failed:', error);
    },
  });

  useEffect(() => {
    if (!router.isReady) return;

    const attribution = getExtTalentBrandDealAttributionFromQuery(router.query);
    if (!attribution) return;

    const key =
      `${attribution.campaignId}:${attribution.extTalentId}:` +
      `${attribution.actionId ?? ''}:${attribution.contactId ?? ''}`;
    if (lastFiredKeyRef.current === key) return;
    lastFiredKeyRef.current = key;

    setExtTalentBrandDealAttributionToStorage(attribution);

    trackExtTalentCtaClick({
      variables: {
        input: {
          campaign: 'bd',
          campaignId: attribution.campaignId,
          extTalentId: attribution.extTalentId,
          ...(attribution.actionId && { actionId: attribution.actionId }),
          ...(attribution.contactId && { contactId: attribution.contactId }),
        },
      },
    }).catch(() => {
      // Already reported via the useMutation onError handler above; this
      // catch only exists to prevent an unhandled promise rejection.
    });

    if (!token) {
      setSignUpModalVisibility(true);
    }
  }, [
    router.isReady,
    router.query,
    token,
    setSignUpModalVisibility,
    trackExtTalentCtaClick,
  ]);
};
